import {
  NavLink,
  useParams,
  Outlet,
  useLocation,
  generatePath,
} from 'react-router-dom';
import { Table, IconExpand, PermissionEnum, PermissionDenied } from '@ui/src';
import { useAppSelector } from '@store';
import {
  linkedClientsColumnsItems,
  linkedClientsColumnsLabels,
} from './variables/linked-clients';
import { CustomerStatus, OrganizationType } from '../types';
import useLinkedClients from './hooks/use-linked-clients';

import ROUTES from '../../../routes';

import cn from './styles.module.css';

const CustomerLinkedClients = ({ type }: { type: OrganizationType }) => {
  const { customerId } = useParams();

  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const {
    pspPartners,
    pspIntegrations,
    linkedClients,
    linkedClientsPagination,
    linkedClientsErrorsList,
  } = useAppSelector((state) => state.customersExternalAccounts);
  const { activePermissions } = useAppSelector((state) => state.root);
  const { search } = useLocation();

  const {
    linkedClientsFormattedData,
    filters,
    onFilter,
    filtersComponent,
    handleSubmit,
    linkedClientsIsLoading,
    linkedClientsIsFetching,
    setPage,
  } = useLinkedClients({
    customerId,
    linkedClients,
    pspPartners,
    pspIntegrations,
    search,
    activePermissions,
  });

  const organizationStatus =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  const link =
    type === OrganizationType.INDIVIDUAL
      ? ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_LINKED_CLIENTS_CREATE
      : ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_LINKED_CLIENTS_CREATE;

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
    )
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        {activePermissions?.includes(
          PermissionEnum.EXTERNAL_ORGANIZATIONS_CREATE_ACTION,
        ) &&
          organizationStatus !== CustomerStatus.CLOSED && (
            <div className={cn.addLinkedClientsWrapper}>
              <NavLink
                to={`${generatePath(link, {
                  customerId: (customerId as string) || '',
                })}${search || ''}`}
                className={cn.addLinkedClientsLink}
              >
                <IconExpand width={16} height={16} />
                Link Customer
              </NavLink>
            </div>
          )}
        <div className={cn.linkedClientsTable}>
          <Table
            columns={linkedClientsColumnsItems}
            data={linkedClientsFormattedData || []}
            onSort={() => {}}
            sort={{}}
            filters={filters}
            columnsLabels={linkedClientsColumnsLabels}
            onFilterChange={(values) => onFilter(values)}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={linkedClientsIsLoading || linkedClientsIsFetching}
            pagination={linkedClientsPagination}
            tableBodyClassName={
              linkedClientsIsLoading || linkedClientsIsFetching
                ? cn.tableLoading
                : ''
            }
            error={linkedClientsErrorsList}
            take="20"
            tableTopClassName={
              Object.keys(filters)?.length ? cn.linkedClientsWithPadding : ''
            }
            setPage={(data) => {
              setPage({
                ...data,
                filters,
              });
            }}
          />
        </div>
        <Outlet />
      </>
    );
  }

  return content;
};

export default CustomerLinkedClients;
